import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { EncounterCard } from "@/components/EncounterCard";
import { Search, Filter, Loader2 } from "lucide-react";
import { KNOWN_ENTITY_TYPES, ENCOUNTER_TYPES, EVIDENCE_LEVELS } from "@/data/formOptions";

const SORT_OPTIONS = [
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
    { value: "most_viewed", label: "Most Viewed" },
    { value: "most_liked", label: "Most Liked" },
    { value: "highest_rated", label: "Highest Rated" },
    { value: "most_credible", label: "Most Credible" },
    { value: "most_discussed", label: "Most Discussed" },
    { value: "popular", label: "Most Popular" },
];

export default function Archive() {
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const q = params.get("q") || "";
    const sort = params.get("sort") || "newest";
    const entity_type = params.get("entity_type") || "";
    const encounter_type = params.get("encounter_type") || "";
    const evidence_level = params.get("evidence_level") || "";
    const country = params.get("country") || "";
    const source_type = params.get("source_type") || "";
    const min_credibility = parseInt(params.get("min_credibility") || "0", 10);
    const tag = params.get("tag") || "";

    useEffect(() => {
        setLoading(true);
        api.get("/encounters", {
            params: { q, sort, entity_type, encounter_type, evidence_level, country, source_type, min_credibility, tag, limit: 48 },
        })
            .then((r) => {
                setItems(r.data.items || []);
            })
            .finally(() => setLoading(false));
    }, [q, sort, entity_type, encounter_type, evidence_level, country, source_type, min_credibility, tag]);

    const update = (key, value) => {
        const next = new URLSearchParams(params);
        if (value === "" || value == null) next.delete(key);
        else next.set(key, value);
        setParams(next, { replace: true });
    };

    return (
        <div className="ok-container py-10" data-testid="archive-page">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                <div>
                    <h1 className="font-display text-3xl sm:text-4xl">Encounter Archive</h1>
                    <p className="text-sm opacity-60 mt-1 font-mono uppercase tracking-widest">Declassified records · {sort.replace("_", " ")}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 opacity-50" />
                    <select
                        value={sort}
                        onChange={(e) => update("sort", e.target.value)}
                        className="ok-input w-auto py-2 text-sm"
                        data-testid="archive-sort-select"
                    >
                        {SORT_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Search */}
            <div className="relative max-w-md mb-6">
                <input
                    value={q}
                    onChange={(e) => update("q", e.target.value)}
                    placeholder="Search by title, location, witness…"
                    className="ok-input pl-10 py-2.5"
                    data-testid="archive-search-input"
                />
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Filter Sidebar */}
                <aside className="lg:col-span-3 ok-card p-5 h-fit" data-testid="archive-filters">
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-3">Filters</div>
                    <div className="space-y-4">
                        <Filter1 label="Source">
                            <select value={source_type} onChange={(e) => update("source_type", e.target.value)} className="ok-input text-sm" data-testid="filter-source-type">
                                <option value="">All sources</option>
                                <option value="historical">Historical archive</option>
                                <option value="user">User submitted</option>
                            </select>
                        </Filter1>
                        <Filter1 label="Entity type">
                            <select value={entity_type} onChange={(e) => update("entity_type", e.target.value)} className="ok-input text-sm" data-testid="filter-entity-type">
                                <option value="">All entities</option>
                                {KNOWN_ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Filter1>
                        <Filter1 label="Encounter type">
                            <select value={encounter_type} onChange={(e) => update("encounter_type", e.target.value)} className="ok-input text-sm" data-testid="filter-encounter-type">
                                <option value="">All types</option>
                                {ENCOUNTER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Filter1>
                        <Filter1 label="Evidence level">
                            <select value={evidence_level} onChange={(e) => update("evidence_level", e.target.value)} className="ok-input text-sm" data-testid="filter-evidence-level">
                                <option value="">All evidence</option>
                                {EVIDENCE_LEVELS.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Filter1>
                        <Filter1 label="Country">
                            <input value={country} onChange={(e) => update("country", e.target.value)} placeholder="e.g. Brazil" className="ok-input text-sm" data-testid="filter-country" />
                        </Filter1>
                        <Filter1 label={`Min credibility: ${min_credibility}`}>
                            <input
                                type="range" min="0" max="100" step="5"
                                value={min_credibility}
                                onChange={(e) => update("min_credibility", e.target.value)}
                                className="w-full"
                                data-testid="filter-min-credibility"
                            />
                        </Filter1>
                        <button onClick={() => setParams({})} className="ok-btn-outline w-full justify-center" data-testid="filter-clear-btn">
                            Clear all
                        </button>
                    </div>
                </aside>

                <div className="lg:col-span-9">
                    {loading ? (
                        <div className="flex items-center justify-center py-20" data-testid="archive-loading">
                            <Loader2 className="w-6 h-6 animate-spin text-[var(--ok-accent)]" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="ok-card p-12 text-center" data-testid="archive-empty">
                            <div className="font-display text-xl mb-2">No encounters match</div>
                            <p className="opacity-60 text-sm">Try widening your filters or search.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="archive-grid">
                            {items.map((e) => <EncounterCard key={e.id} e={e} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Filter1({ label, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 mb-1.5">{label}</div>
            {children}
        </div>
    );
}
